import styled from 'styled-components'
import { SpaceProps, space, variant } from 'styled-system'

import ArrowLong from '@/atoms/icons/arrow-long.png'
import ArrowShort from '@/atoms/icons/arrow-short.png'
import { filterSpaceProps } from '@/helpers/styled-props'
import { Theme } from '@/styles/theme'

export enum ButtonVariant {
  DEFAULT = 'DEFAULT',
  INVERTED = 'INVERTED',
  LIGHT = 'LIGHT',
  LIGHT_INVERTED = 'LIGHT_INVERTED',
  ERROR = 'ERROR',
  ERROR_INVERTED = 'ERROR_INVERTED',
  FLAT = 'FLAT',
  FLAT_ERROR = 'FLAT_ERROR',
  INVERTED_FLAT_ERROR = 'INVERTED_FLAT_ERROR',
  TABLE_DEFAULT_ACTION = 'TABLE_DEFAULT_ACTION',
  TABLE_ERROR_ACTION = 'TABLE_ERROR_ACTION',
  TABLE_LIGHT_ACTION = 'TABLE_LIGHT_ACTION',
  PUBLIC = 'PUBLIC',
}

export enum ButtonSize {
  SMALL = 'small',
  DEFAULT = 'default',
  AUTO = 'auto',
}

interface OwnProps {
  $variant?: ButtonVariant
  $size?: ButtonSize
  $fullWidth?: boolean
}

declare type ButtonProps = SpaceProps & OwnProps

const ButtonVariants = ({ theme }: { theme: Theme }) =>
  variant({
    prop: '$variant',
    variants: {
      [ButtonVariant.DEFAULT]: {
        color: theme.colors.violet,
        '&:not(:disabled):hover': {
          bg: theme.colors.bgVioletLight,
        },
      },
      [ButtonVariant.INVERTED]: {
        color: theme.colors.violet,
        bg: theme.colors.statusViolet,
        '&:not(:disabled):hover': {
          bg: 'transparent',
        },
      },
      [ButtonVariant.TABLE_DEFAULT_ACTION]: {
        color: theme.colors.violet,
        border: `.5px solid ${theme.colors.violet}`,
        borderRadius: '4px',
        fontWeight: 400,
        textTransform: 'none',
        '&:not(:disabled):hover': {
          bg: theme.colors.bgVioletLight,
        },
        '&:disabled': {
          color: theme.colors.grey,
          borderColor: theme.colors.grey,
        },
      },
      [ButtonVariant.LIGHT]: {
        color: theme.colors.label,
        '&:not(:disabled):hover': {
          bg: theme.colors.bgGreyLight,
        },
      },
      [ButtonVariant.LIGHT_INVERTED]: {
        bg: theme.colors.bgGreyLight,
        color: theme.colors.label,
        '&:not(:disabled):hover': {
          bg: 'transparent',
        },
      },
      [ButtonVariant.TABLE_LIGHT_ACTION]: {
        color: theme.colors.label,
        border: `.5px solid ${theme.colors.label}`,
        borderRadius: '4px',
        fontWeight: 400,
        textTransform: 'none',
        '&:not(:disabled):hover': {
          bg: theme.colors.bgGreyLight,
        },
        '&:disabled': {
          color: theme.colors.grey,
          borderColor: theme.colors.grey,
        },
      },
      [ButtonVariant.ERROR]: {
        color: theme.colors.error,
        '&:not(:disabled):hover': {
          bg: theme.colors.errorBackground,
        },
      },
      [ButtonVariant.ERROR_INVERTED]: {
        color: theme.colors.error,
        bg: theme.colors.errorBackground,
        '&:not(:disabled):hover': {
          bg: 'transparent',
        },
      },
      [ButtonVariant.TABLE_ERROR_ACTION]: {
        color: theme.colors.error,
        border: `.5px solid ${theme.colors.error}`,
        borderRadius: '4px',
        fontWeight: 400,
        textTransform: 'none',
        '&:not(:disabled):hover': {
          bg: theme.colors.errorBackground,
        },
        '&:disabled': {
          color: theme.colors.grey,
          borderColor: theme.colors.grey,
        },
      },
      [ButtonVariant.FLAT]: {
        color: theme.colors.violetLight,
        textTransform: 'unset',
        fontWeight: 400,
        minWidth: 'auto',
        px: 0,
        '&:not(:disabled):hover': {
          color: theme.colors.label,
        },
      },
      [ButtonVariant.FLAT_ERROR]: {
        color: theme.colors.label,
        textTransform: 'unset',
        fontWeight: 400,
        minWidth: 'auto',
        px: 0,
        '&:not(:disabled):hover': {
          color: theme.colors.error,
        },
      },
      [ButtonVariant.INVERTED_FLAT_ERROR]: {
        color: theme.colors.error,
        textTransform: 'unset',
        fontWeight: 400,
        minWidth: 'auto',
        px: 0,
      },
      [ButtonVariant.PUBLIC]: {
        color: theme.colors.heading,
        bg: theme.colors.publicOrange,
        textTransform: 'unset',
        fontSize: theme.fonts.fontSize16,
        letterSpacing: '1.25px',
        borderRadius: '50px',
        height: '52px',
        px: 0,
        '&:not(:disabled):after': {
          content: '""',
          backgroundImage: `url(${ArrowShort})`,
          backgroundSize: '100% 100%',
          left: 'auto',
          marginLeft: theme.space.S8,
          height: '6px',
          width: '12px',
        },
        '&:not(:disabled):hover, &:focus': {
          '&:after': {
            backgroundImage: `url(${ArrowLong})`,
            width: '24px',
          },
        },
      },
    },
  })

const ButtonSizes = ({ theme }: { theme: Theme }) =>
  variant({
    prop: '$size',
    variants: {
      [ButtonSize.AUTO]: {
        height: 'auto',
        px: 0,
        fontSize: theme.fonts.fontSize12,
      },
      [ButtonSize.SMALL]: {
        height: '24px',
        px: theme.space.S8,
        fontSize: theme.fonts.fontSize12,
      },
      [ButtonSize.DEFAULT]: {
        height: '36px',
        px: theme.space.S12,
        fontSize: theme.fonts.fontSize14,
      },
    },
  })

export const StyledButton = styled.button.withConfig({
  shouldForwardProp: filterSpaceProps(),
})<ButtonProps>`
  display: inline-flex;
  position: relative;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  cursor: pointer;
  outline: none;
  border: none;
  letter-spacing: ${({ theme }) => theme.fonts.letterSpacing};
  font-family: ${({ theme }) => theme.fonts.textFont};
  font-weight: ${({ theme }) => theme.fonts.fontWeightMedium};
  text-transform: uppercase;
  background-color: transparent;
  transition: 0.3s all ease;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  min-width: 64px;

  &:disabled {
    color: ${({ theme }) => theme.colors.grey};
    cursor: not-allowed;
  }

  ${ButtonSizes}
  ${ButtonVariants}
  ${space}
`

export default StyledButton
